/**
 * Fixed bottom tab bar — the primary navigation for authenticated users.
 *
 * Two tabs for v1.0: "Ordens de Servico" and "Notificacoes".
 * Each tab is a React Router <NavLink> with a 44px minimum tap target.
 * Safe-area-inset-bottom for notched phones.
 */
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { currentLocale } from '../i18n/index.ts';
import { buildPath } from '../i18n/routes.ts';
import { IconOrders, IconBell } from './icons.tsx';
import styles from './BottomTabBar.module.css';

interface Props {
  unreadCount: number;
}

export function BottomTabBar({ unreadCount }: Props): React.JSX.Element {
  const { t } = useTranslation();
  // Built, never literal: the slug is translated, so a hardcoded `/notificacoes`
  // would 404 the moment a Chilean customer tapped it.
  const locale = currentLocale();
  return (
    <nav className={styles['bar']} aria-label={t('nav.aria')}>
      <NavLink
        to={buildPath('ordensServico', locale)}
        className={({ isActive }) =>
          `${styles['tab']} ${isActive ? styles['tab-active'] : ''}`
        }
        end
      >
        <IconOrders size={22} />
        <span className={styles['label']}>{t('nav.orders')}</span>
      </NavLink>

      <NavLink
        to={buildPath('notificacoes', locale)}
        className={({ isActive }) =>
          `${styles['tab']} ${isActive ? styles['tab-active'] : ''}`
        }
        end
      >
        <span className={styles['icon-wrap']}>
          <IconBell size={22} />
          {unreadCount > 0 && (
            <span className={styles['badge']}>
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </span>
        <span className={styles['label']}>{t('nav.notifications')}</span>
      </NavLink>
    </nav>
  );
}
